import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useDashboardSearchParams } from "@/hooks/useDashboardNavigation";
import { ADMIN_SECTIONS, type AdminTabValue, adminTabFromParam, adminTabHref } from "@/components/admin/adminSections";
import { cn } from "@repo/blacksmith-ui/cn";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "@repo/blacksmith-ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { SidebarHoverCardMenuItem } from "./SidebarHoverCardMenuItem";
import { SIDEBAR_NAV_BUTTON_CLASS, SIDEBAR_NAV_ITEM_CLASS } from "@repo/blacksmith-ui/sidebar-nav";

/** The admin tab the current route shows; the internal-monitors org pages belong to Internal Monitors. */
function useActiveAdminTab() {
  const $ = _c(3);
  const router = useRouter();
  const t0 = useDashboardSearchParams();
  let t1;
  if ($[0] !== router.pathname || $[1] !== t0) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const tabParam = t0.get("tab");
      if (router.pathname === "/admin") {
        t1 = adminTabFromParam(tabParam).value;
        break bb0;
      }
    }
    $[0] = router.pathname;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  if (router.pathname.startsWith("/admin/internal-monitors")) {
    return "internal-monitors";
  }
  return null;
}

/**
 * Contextual sidebar for the superuser admin routes. Sits to the right of the
 * app-switcher rail with the same rows as the GitHub Actions sidebar; Proof
 * Analysis opens its pages in a hover card like Run History does.
 */
export default function AdminSidebar() {
  const $ = _c(10);
  const activeTab = useActiveAdminTab();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Admin" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeTab) {
    t2 = ADMIN_SECTIONS.map(section => {
      const Icon = section.icon;
      if ("subTabs" in section) {
        return <SidebarHoverCardMenuItem key={section.label} name={section.label} icon={<Icon className="size-[18px]" />} subItems={section.subTabs.map(subTab => ({
          name: subTab.label,
          href: adminTabHref(subTab.value),
          current: subTab.value === activeTab,
          icon: <subTab.icon className="size-4" />
        }))} prefetchOnIntent={false} />;
      }
      return <SidebarMenuItem key={section.value} className={SIDEBAR_NAV_ITEM_CLASS}><SidebarMenuButton asChild={true} isActive={section.value === activeTab} tooltip={section.label} className={SIDEBAR_NAV_BUTTON_CLASS}><RoutePrefetchLink href={adminTabHref(section.value)} prefetchOnIntent={false} aria-label={section.label} className="cursor-pointer"><div className="relative"><Icon className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{section.label}</span></RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem>;
    });
    $[2] = activeTab;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <SidebarContent className="flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden"><SidebarGroup className="border-0 min-h-0 flex-1 overflow-y-auto pt-[calc(var(--spacing-app-header-gap)-0.5rem)] group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className="flex flex-col gap-1">{t2}</SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarContent>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0" />;
    t5 = <SidebarRail />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3) {
    t6 = <ShadcnSidebar collapsible="icon" className={t0}>{t1}{t3}{t4}{t5}</ShadcnSidebar>;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}